/**
 * Plan Recommendation Algorithm
 * ==============================
 *
 * This module implements a sophisticated plan recommendation system that analyzes
 * user needs and suggests the most appropriate Buffer plan. The algorithm balances
 * multiple factors to provide intelligent, context-aware recommendations.
 *
 * How It Works:
 * -------------
 * 1. **User Analysis**: We examine the user's current usage patterns including:
 *    - Number of social channels they manage
 *    - Team size (number of users)
 *    - Current plan and payment history
 *    - Advanced feature requirements
 *
 * 2. **Feature Matching**: Each plan offers different features. We determine which
 *    features the user needs based on their usage and match them against what each
 *    plan provides.
 *
 * 3. **Multi-Factor Scoring**: Plans are scored across four dimensions:
 *    - Feature Match (40%): Does the plan have all required features?
 *    - Price Efficiency (30%): Best value for the user's channel count
 *    - Growth Headroom (20%): Room to scale without needing to upgrade
 *    - Plan Distance (10%): Preference for minimal change from current plan
 *
 * 4. **Penalty System**: Instead of hard disqualification, we apply penalties for:
 *    - Plans that can't support the required channel count
 *    - Plans that exceed budget constraints (if specified)
 *
 * 5. **Fallback Logic**: Multiple safety nets ensure we ALWAYS return a recommendation:
 *    - Primary: Full scoring algorithm with pricing data
 *    - Secondary: Heuristic-based quick recommendation
 *    - Tertiary: Sensible defaults based on channel count
 *
 * Plan Hierarchy:
 * --------------
 * Free → Essentials → Team → Agency
 *
 * The recommendation is consistent across billing cycles (monthly/yearly) to avoid
 * confusion, though pricing calculations account for yearly savings.
 */

import type { ObPlanId } from '../../../gql/graphql'

import { FREE_PLAN_CHANNEL_LIMIT } from '../constants'
import { OBPlanIdValues } from '../types'
import type { ExtendedPlanOption } from './dataTransformers'

const FEATURE_IDS = {
  UNLIMITED_POSTS: 'posts',
  UNLIMITED_IDEAS: 'ideas',
  UNLIMITED_USERS: 'users',
  THREADS: 'threads',
  BASIC_ANALYTICS: 'analytics',
  ADVANCED_ANALYTICS: 'analytics',
  ENGAGEMENT: 'engagement',
  HASHTAGS: 'hashtags',
  FIRST_COMMENT: 'comment',
  ACCESS_LEVELS: 'access',
  APPROVAL_WORKFLOWS: 'approval',
  NOTES: 'notes',
} as const

export interface RecommendationFactors {
  channelCount: number
  userCount?: number
  currentPlanId: ObPlanId
  requiredFeatures?: string[]
  needsAdvancedAnalytics?: boolean
  needsTeamFeatures?: boolean
}

const SCORING_WEIGHTS = {
  FEATURE_MATCH: 0.4,
  PRICE_EFFICIENCY: 0.3,
  GROWTH_HEADROOM: 0.2,
  PLAN_DISTANCE: 0.1,
}

interface RecommendationResult {
  recommendedPlanId: ObPlanId
  score: number
  reasoning: string[]
}

function getPlanFeatureIds(planId: ObPlanId): Set<string> {
  const features = new Set<string>()

  switch (planId) {
    case OBPlanIdValues.free:
      features.add(FEATURE_IDS.THREADS)
      features.add(FEATURE_IDS.BASIC_ANALYTICS)
      break

    case OBPlanIdValues.essentials:
      features.add(FEATURE_IDS.UNLIMITED_POSTS)
      features.add(FEATURE_IDS.UNLIMITED_IDEAS)
      features.add(FEATURE_IDS.ADVANCED_ANALYTICS)
      features.add(FEATURE_IDS.ENGAGEMENT)
      features.add(FEATURE_IDS.HASHTAGS)
      features.add(FEATURE_IDS.FIRST_COMMENT)
      break

    case OBPlanIdValues.team:
      features.add(FEATURE_IDS.UNLIMITED_POSTS)
      features.add(FEATURE_IDS.UNLIMITED_IDEAS)
      features.add(FEATURE_IDS.UNLIMITED_USERS)
      features.add(FEATURE_IDS.ADVANCED_ANALYTICS)
      features.add(FEATURE_IDS.ENGAGEMENT)
      features.add(FEATURE_IDS.HASHTAGS)
      features.add(FEATURE_IDS.FIRST_COMMENT)
      features.add(FEATURE_IDS.ACCESS_LEVELS)
      features.add(FEATURE_IDS.APPROVAL_WORKFLOWS)
      features.add(FEATURE_IDS.NOTES)
      break
  }

  return features
}

function calculateFeatureScore(
  planId: ObPlanId,
  requiredFeatures: string[],
): number {
  if (requiredFeatures.length === 0) return 1

  const planFeatures = getPlanFeatureIds(planId)
  const matchedFeatures = requiredFeatures.filter((f) => planFeatures.has(f))

  return matchedFeatures.length / requiredFeatures.length
}

function calculatePriceEfficiencyScore(
  plan: ExtendedPlanOption,
  channelCount: number,
): number {
  const { totalPrice } = plan.calculations

  if (totalPrice === 0) return 1

  const pricePerChannel = totalPrice / channelCount
  const baseline = 1000 // $10 in cents
  const score = Math.min(1, baseline / pricePerChannel)

  return score
}

function calculateGrowthScore(planId: ObPlanId, channelCount: number): number {
  switch (planId) {
    case OBPlanIdValues.free:
      if (channelCount > FREE_PLAN_CHANNEL_LIMIT) return 0
      return (FREE_PLAN_CHANNEL_LIMIT - channelCount) / FREE_PLAN_CHANNEL_LIMIT

    case OBPlanIdValues.essentials:
      return 1

    case OBPlanIdValues.team:
      return 1

    default:
      return 0.5
  }
}

function calculatePlanDistanceScore(
  targetPlanId: ObPlanId,
  currentPlanId: ObPlanId,
): number {
  const planOrder = [
    OBPlanIdValues.free,
    OBPlanIdValues.essentials,
    OBPlanIdValues.team,
    OBPlanIdValues.agency,
  ]

  const currentIndex = planOrder.indexOf(currentPlanId)
  const targetIndex = planOrder.indexOf(targetPlanId)

  if (currentIndex === -1 || targetIndex === -1) return 0.5

  const distance = Math.abs(targetIndex - currentIndex)
  return Math.max(0.25, 1 - distance * 0.25)
}

function determineRequiredFeatures(factors: RecommendationFactors): string[] {
  const features: string[] = []

  if (factors.channelCount > FREE_PLAN_CHANNEL_LIMIT) {
    features.push(FEATURE_IDS.UNLIMITED_POSTS)
    features.push(FEATURE_IDS.UNLIMITED_IDEAS)
  }

  if (factors.userCount && factors.userCount > 1) {
    features.push(FEATURE_IDS.UNLIMITED_USERS)
    features.push(FEATURE_IDS.ACCESS_LEVELS)
  }

  if (factors.needsAdvancedAnalytics) {
    features.push(FEATURE_IDS.ADVANCED_ANALYTICS)
    features.push(FEATURE_IDS.ENGAGEMENT)
  }

  if (factors.needsTeamFeatures) {
    features.push(FEATURE_IDS.APPROVAL_WORKFLOWS)
    features.push(FEATURE_IDS.NOTES)
  }

  if (factors.requiredFeatures) {
    features.push(...factors.requiredFeatures)
  }

  return [...new Set(features)]
}

function calculatePlanScore(
  plan: ExtendedPlanOption,
  factors: RecommendationFactors,
  requiredFeatures: string[],
): number {
  let penalty = 1.0

  if (
    factors.channelCount > FREE_PLAN_CHANNEL_LIMIT &&
    plan.planId === OBPlanIdValues.free
  ) {
    penalty *= 0.1
  }

  const featureScore = calculateFeatureScore(plan.planId, requiredFeatures)
  const priceScore = calculatePriceEfficiencyScore(plan, factors.channelCount)
  const growthScore = calculateGrowthScore(plan.planId, factors.channelCount)
  const distanceScore = calculatePlanDistanceScore(
    plan.planId,
    factors.currentPlanId,
  )

  const baseScore =
    featureScore * SCORING_WEIGHTS.FEATURE_MATCH +
    priceScore * SCORING_WEIGHTS.PRICE_EFFICIENCY +
    growthScore * SCORING_WEIGHTS.GROWTH_HEADROOM +
    distanceScore * SCORING_WEIGHTS.PLAN_DISTANCE

  return baseScore * penalty
}

function generateRecommendationReasoning(
  planId: ObPlanId,
  factors: RecommendationFactors,
): string[] {
  const reasons: string[] = []

  switch (planId) {
    case OBPlanIdValues.free:
      reasons.push('Perfect for getting started with Buffer')
      reasons.push(`Supports up to ${FREE_PLAN_CHANNEL_LIMIT} channels`)
      reasons.push('Includes basic analytics and scheduling')
      break

    case OBPlanIdValues.essentials:
      reasons.push('Best value for individual creators and businesses')
      if (factors.channelCount > FREE_PLAN_CHANNEL_LIMIT) {
        reasons.push(
          `Supports your ${factors.channelCount} channels with unlimited posts`,
        )
      }
      if (factors.needsAdvancedAnalytics) {
        reasons.push('Includes advanced analytics and engagement tools')
      }
      reasons.push('Great pricing for channels at your scale')
      break

    case OBPlanIdValues.team:
      reasons.push('Ideal for teams and agencies')
      if (factors.userCount && factors.userCount > 1) {
        reasons.push(
          `Supports unlimited team members (you need ${factors.userCount})`,
        )
      }
      if (factors.needsTeamFeatures) {
        reasons.push('Includes approval workflows and collaboration tools')
      }
      if (factors.channelCount > 25) {
        reasons.push('Best pricing for high channel counts')
      }
      break
  }

  return reasons
}

export function calculateRecommendedPlan(
  plans: ExtendedPlanOption[],
  factors: RecommendationFactors,
): RecommendationResult {
  if (!plans || plans.length === 0) {
    const fallbackPlanId = getQuickRecommendation(
      factors.channelCount,
      factors.userCount || 1,
      factors.needsAdvancedAnalytics || false,
    )

    return {
      recommendedPlanId: fallbackPlanId,
      score: 0.5,
      reasoning: ['Recommended based on your requirements'],
    }
  }

  const requiredFeatures = determineRequiredFeatures(factors)

  const uniquePlans = plans.reduce<ExtendedPlanOption[]>((acc, plan) => {
    if (!acc.some((p) => p.planId === plan.planId)) {
      acc.push(plan)
    }
    return acc
  }, [])

  if (uniquePlans.length === 0) {
    const fallbackPlan = plans[0]
    return {
      recommendedPlanId: fallbackPlan.planId,
      score: 0.5,
      reasoning: ['Selected as the available option for your needs'],
    }
  }

  const planScores = uniquePlans.map((plan) => ({
    plan,
    score: calculatePlanScore(plan, factors, requiredFeatures),
  }))

  planScores.sort((a, b) => b.score - a.score)

  const bestPlan = planScores[0]

  if (bestPlan.score < 0.1) {
    let fallbackPlanId: ObPlanId

    if (factors.channelCount <= FREE_PLAN_CHANNEL_LIMIT) {
      fallbackPlanId = OBPlanIdValues.free
    } else {
      fallbackPlanId = OBPlanIdValues.essentials
    }

    const fallbackExists = uniquePlans.some((p) => p.planId === fallbackPlanId)
    if (fallbackExists) {
      return {
        recommendedPlanId: fallbackPlanId,
        score: 0.3,
        reasoning: generateRecommendationReasoning(fallbackPlanId, factors),
      }
    }
  }

  const reasoning = generateRecommendationReasoning(
    bestPlan.plan.planId,
    factors,
  )

  return {
    recommendedPlanId: bestPlan.plan.planId,
    score: bestPlan.score,
    reasoning,
  }
}

export function getQuickRecommendation(
  channelCount: number,
  userCount: number = 1,
  needsAdvancedFeatures: boolean = false,
): ObPlanId {
  const validChannelCount = Math.max(1, channelCount)
  const validUserCount = Math.max(1, userCount)

  // Only recommend Team plan if user count > 1, not based on channel count alone
  if (validUserCount > 1) {
    return OBPlanIdValues.team
  }

  if (needsAdvancedFeatures || validChannelCount > FREE_PLAN_CHANNEL_LIMIT) {
    return OBPlanIdValues.essentials
  }

  if (validChannelCount <= FREE_PLAN_CHANNEL_LIMIT) {
    return OBPlanIdValues.free
  }

  return OBPlanIdValues.essentials
}
